"use client";

import type React from "react";

import { Button } from "@papier/ui/components/ui/button";
import { Card, CardContent } from "@papier/ui/components/ui/card";
import { Badge } from "@papier/ui/components/ui/badge";
import { ChevronDown, ChevronRight, GripVertical, List, Trash2 } from "lucide-react";
import { fieldTypes } from "@/constants/field-types";
import clsx from "clsx";
import { useMemo } from "react";
import {
  DragState,
  SchemaField,
} from "@papier/grapesjs-editor/src/components/editor/schema-builder/types/schema";

interface SchemaFieldProps {
  field: SchemaField;
  depth?: number;
  isArrayItem?: boolean;
  selectedField?: SchemaField | null;
  dragState?: DragState;
  readOnly?: boolean;
  onSelect?: (field: SchemaField) => void;
  onUpdate?: (id: string, updates: Partial<SchemaField>) => void;
  onDelete?: (id: string) => void;
  onDragStart?: (e: React.DragEvent, field: SchemaField) => void;
  onDragEnter?: (
    e: React.DragEvent,
    targetId: string,
    position: "before" | "after" | "inside",
  ) => void;
  onDragLeave?: (e: React.DragEvent) => void;
  onDragOver?: (e: React.DragEvent) => void;
  onDrop?: (
    e: React.DragEvent,
    targetId?: string,
    position?: "before" | "after" | "inside",
  ) => void;
}

export function SchemaFieldComponent({
  field,
  depth = 0,
  isArrayItem = false,
  selectedField,
  dragState,
  readOnly = false,
  onSelect,
  onUpdate,
  onDelete,
  onDragStart,
  onDragEnter,
  onDragLeave,
  onDragOver,
  onDrop,
}: SchemaFieldProps) {
  const fieldType = useMemo(() => {
    return fieldTypes.find((f) => f.value === field.type);
  }, [field.type]);
  if (!fieldType) {
    console.warn(`Unknown field type: ${field.type}`);
    return null;
  }
  const Icon = fieldType.icon;
  const colorClass = fieldType.color;

  const isDragTarget = dragState?.dropZone?.id === field.id;
  const isDragging = dragState?.draggedField?.id === field.id;
  const isObjectDropTarget =
    field.type === "object" && isDragTarget && dragState?.dropZone?.position === "inside";

  const renderField = (
    field: SchemaField,
    depth: number,
    isArrayItem: boolean,
  ): React.ReactNode => (
    <SchemaFieldComponent
      key={field.id}
      field={field}
      depth={depth}
      isArrayItem={isArrayItem}
      selectedField={selectedField}
      dragState={dragState}
      readOnly={readOnly}
      onSelect={onSelect}
      onUpdate={onUpdate}
      onDelete={onDelete}
      onDragStart={onDragStart}
      onDragEnter={onDragEnter}
      onDragLeave={onDragLeave}
      onDragOver={onDragOver}
      onDrop={onDrop}
    />
  );

  return (
    <div>
      {!readOnly && !isArrayItem && (
        <div
          className={`h-1 transition-all ${
            isDragTarget && dragState?.dropZone?.position === "before" ? "rounded bg-blue-400" : ""
          }`}
          onDragEnter={onDragEnter ? (e) => onDragEnter(e, field.id, "before") : undefined}
          onDragLeave={onDragLeave}
          onDragOver={onDragOver}
          onDrop={onDrop ? (e) => onDrop(e, field.id, "before") : undefined}
        />
      )}

      <Card
        className={clsx(
          "transition-all",
          !readOnly && "cursor-pointer hover:shadow-md",
          selectedField?.id === field.id && "ring-2 ring-blue-500",
          isObjectDropTarget && "bg-green-50 ring-2 ring-green-500",
          isDragging && "opacity-50",
          "py-2",
        )}
        style={{ marginLeft: depth * 16 }}
        onClick={onSelect ? () => onSelect(field) : undefined}
        draggable={!readOnly && !isArrayItem}
        onDragStart={!readOnly && onDragStart ? (e) => onDragStart(e, field) : undefined}
      >
        <CardContent
          className={`p-4 ${field.type === "object" && !readOnly ? "border-2 border-dashed border-transparent" : ""} ${
            isObjectDropTarget ? "border-green-400 bg-green-50" : ""
          }`}
          onDragEnter={
            !readOnly && field.type === "object" && dragState?.isDragging && onDragEnter
              ? (e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onDragEnter(e, field.id, "inside");
                }
              : undefined
          }
          onDragLeave={
            !readOnly && field.type === "object" && onDragLeave
              ? (e) => {
                  e.stopPropagation();
                  onDragLeave(e);
                }
              : undefined
          }
          onDragOver={
            !readOnly && field.type === "object" && dragState?.isDragging && onDragOver
              ? (e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  e.dataTransfer.dropEffect = dragState.draggedFieldType ? "copy" : "move";
                }
              : undefined
          }
          onDrop={
            !readOnly && field.type === "object" && onDrop
              ? (e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onDrop(e, field.id, "inside");
                }
              : undefined
          }
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {!readOnly && !isArrayItem && (
                <div className="cursor-grab active:cursor-grabbing">
                  <GripVertical className="h-4 w-4" />
                </div>
              )}
              {(field.type === "object" || field.type === "array") && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (onUpdate) onUpdate(field.id, { expanded: !field.expanded });
                  }}
                >
                  {field.expanded ? (
                    <ChevronDown className="h-4 w-4" />
                  ) : (
                    <ChevronRight className="h-4 w-4" />
                  )}
                </Button>
              )}
              <div className={`rounded-lg border p-2 ${colorClass}`}>
                <Icon className="h-4 w-4" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-medium">{field.name}</span>
                  {isArrayItem && (
                    <Badge variant="secondary" className="text-xs">
                      List Item
                    </Badge>
                  )}
                  {field.required && (
                    <Badge variant="destructive" className="text-xs">
                      Required
                    </Badge>
                  )}
                </div>
                <div className="text-muted-foreground flex items-center gap-2 text-sm">
                  <span className="capitalize">{field.type}</span>
                  {field.type === "array" && field.arrayItemType && (
                    <span>of {field.arrayItemType.type}</span>
                  )}
                  {field.description && <span>• {field.description}</span>}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {!readOnly && !isArrayItem && onDelete && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(field.id);
                  }}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {field.type === "array" && field.expanded && field.arrayItemType && (
        <div className="ml-8 border-l-2 border-dashed border-gray-300 pl-4">
          <div className="text-muted-foreground mb-2 flex items-center gap-2 text-sm">
            <List className="h-4 w-4" />
            List contains:
          </div>
          {renderField(field.arrayItemType, depth + 1, true)}
          {field.arrayItemType.type === "object" &&
            field.arrayItemType.expanded &&
            field.arrayItemType.objectFields && (
              <div className="mt-2 space-y-2">
                {field.arrayItemType.objectFields.map((subField) =>
                  renderField(subField, depth + 2, false),
                )}
              </div>
            )}
        </div>
      )}

      {field.type === "object" && field.expanded && field.objectFields && (
        <div className="space-y-2">
          {field.objectFields.map((subField) => renderField(subField, depth + 1, false))}
        </div>
      )}

      {!readOnly && !isArrayItem && (
        <div
          className={`h-1 transition-all ${
            isDragTarget && dragState?.dropZone?.position === "after" ? "rounded" : ""
          }`}
          onDragEnter={onDragEnter ? (e) => onDragEnter(e, field.id, "after") : undefined}
          onDragLeave={onDragLeave}
          onDragOver={onDragOver}
          onDrop={onDrop ? (e) => onDrop(e, field.id, "after") : undefined}
        />
      )}
    </div>
  );
}
